import { memo } from "react";
import { Box, ToggleButton } from "@mui/material";
import { FoodsIcon, FoodsTooltip } from "@foodpilot/foods";

export type ActionToggleProps = {
    label: string;
    ariaLabel: string;
    active: boolean;
    onToggle: () => void;
    fullWidth?: boolean;
};

const ActionToggleComponent = (props: ActionToggleProps) => {
    const { label, ariaLabel, active, onToggle, fullWidth = false } = props;

    return (
        <FoodsTooltip title={ariaLabel} enterDelay={400}>
            <ToggleButton
                value={label}
                selected={active}
                onChange={onToggle}
                aria-label={ariaLabel}
                aria-pressed={active}
                size="small"
                fullWidth={fullWidth}
                disableRipple
                sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: theme.spacing(0.75),
                    height: theme.spacing(4),
                    minWidth: fullWidth ? "auto" : theme.spacing(10.5),
                    padding: theme.spacing(0, 1.5),
                    borderRadius: theme.spacing(1),
                    textTransform: "none",
                    ...theme.typography["small-bold"],
                    lineHeight: 1,
                    transition: "background-color 150ms, border-color 150ms, color 150ms",
                    "@media (prefers-reduced-motion: reduce)": {
                        transition: "none",
                    },
                    border: `1px solid ${active ? theme.palette.primary[600] : theme.custom.grey[600]}`,
                    color: active ? theme.custom.grey[100] : theme.custom.grey[2500],
                    backgroundColor: active ? theme.palette.primary[600] : theme.custom.grey[100],
                    "&.Mui-selected": {
                        backgroundColor: theme.palette.primary[600],
                        color: theme.custom.grey[100],
                        "&:hover": {
                            backgroundColor: theme.palette.primary[700],
                        },
                    },
                    "&:hover": {
                        backgroundColor: active ? theme.palette.primary[700] : theme.custom.grey[300],
                        borderColor: active ? theme.palette.primary[700] : theme.custom.grey[1000],
                    },
                    "&:focus-visible": {
                        outline: `2px solid ${theme.palette.primary[400]}`,
                        outlineOffset: "1px",
                    },
                })}
            >
                <FoodsIcon icon={active ? "check" : "close"} size={1} />
                <Box component="span" sx={{ fontFamily: "var(--font-mono, monospace)" }}>
                    {label}
                </Box>
            </ToggleButton>
        </FoodsTooltip>
    );
};

export const ActionTogglePlaceholder = () => {
    return (
        <Box
            aria-hidden
            sx={(theme) => ({
                height: theme.spacing(4),
                minWidth: theme.spacing(10.5),
                borderRadius: theme.spacing(1),
                border: `1px dashed ${theme.custom.grey[600]}`,
                backgroundColor: theme.custom.grey[300],
                opacity: 0.5,
            })}
        />
    );
};

export const ActionToggle = memo(ActionToggleComponent);
